import { useEffect, useRef } from 'react';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { TryAgainButton, useNotify } from '../common/components';
import { TextLink } from '../common/components/typography';
import { useConfiguration } from '../configuration';
import translations from './useVisualValidationGeneratingWindow.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory(
  'use-generate-visual-validation',
);
addTranslations(translations);

export const useVisualValidationGeneratingWindow = (windowUrl: string) => {
  const { t } = useTranslation();
  const { supersetUrl } = useConfiguration();
  const notify = useNotify();
  const childWindowRef = useRef<Window | null>(null);

  useEffect(() => {
    const closeChildWindow = () => {
      const childWindow = childWindowRef.current;
      if (childWindow && !childWindow.closed) {
        childWindow.close();
      }
    };

    window.addEventListener('beforeunload', closeChildWindow);
    window.addEventListener('pagehide', closeChildWindow);

    return () => {
      window.removeEventListener('beforeunload', closeChildWindow);
      window.removeEventListener('pagehide', closeChildWindow);
      closeChildWindow();
    };
  }, []);

  return (generateAsync: () => Promise<{ externalCode: string }>) => {
    const generate = () => {
      generateAsync()
        .then(({ externalCode }) => {
          const childWindow = childWindowRef.current;
          const redirectUrl = `${supersetUrl}/superset/dashboard/${externalCode}`;

          if (childWindow) {
            retryPostMessageUntilReceived(childWindow, { redirectUrl });
            // Clear reference to opened window after successful dashboard creation
            // because we expect it to redirect to the given URL and as such we no longer
            // consider it a "child" as there is no further need to communicate with it.
            childWindowRef.current = null;
          } else {
            notify.success({
              message: t('popup-blocked.success'),
              description: (
                <TextLink
                  id="open-visual-validation-dashboard"
                  href={redirectUrl}
                  target="_blank"
                >
                  {redirectUrl}
                </TextLink>
              ),
            });
          }
        })
        .catch((error) => {
          const childWindow = childWindowRef.current;

          if (childWindow) {
            const listener = (event: MessageEvent) => {
              if (isMessage(event, 'try-again')) {
                removeListenerSafely(childWindow, listener);
                generate();
              }
            };
            childWindow.addEventListener('message', listener);

            retryPostMessageUntilReceived(childWindow, {
              error: {
                message: error.message,
                stack: error.response?.data.trace,
              },
            });
          } else {
            throw Object.assign(error, {
              actions: <TryAgainButton onClick={generate} />,
            });
          }
        });
    };

    childWindowRef.current = window.open(windowUrl, '_blank');

    if (!childWindowRef.current) {
      notify.warning({
        message: t('popup-blocked.title'),
        description: t('popup-blocked.description'),
      });
    }

    generate();
  };
};

const retryPostMessageUntilReceived = (
  targetWindow: Window,
  message: any,
  intervalMs = 500,
  maxAttempts = 40,
) => {
  let intervalId: number;
  let attempts = 0;

  const listener = (event: MessageEvent) => {
    if (isMessage(event, 'message-received')) {
      terminate();
    }
  };

  const terminate = () => {
    removeListenerSafely(targetWindow, listener);
    clearInterval(intervalId);
  };

  targetWindow.addEventListener('message', listener);

  intervalId = window.setInterval(() => {
    targetWindow.postMessage(message);
    attempts++;
    if (attempts >= maxAttempts) {
      terminate();
    }
  }, intervalMs);
};

const isMessage = (event: MessageEvent, data: any) =>
  event.origin === window.location.origin && event.data === data;

// Once the child window has navigated cross-origin (e.g. to the Superset
// dashboard after a successful redirect), removeEventListener on its Window
// proxy throws a SecurityError. That's expected here: there is no listener
// left to remove once the child no longer shares our origin.
const removeListenerSafely = (
  targetWindow: Window,
  listener: (event: MessageEvent) => void,
) => {
  try {
    targetWindow.removeEventListener('message', listener);
  } catch {
    // Cross-origin Window proxy; nothing to clean up.
  }
};
